import { gql } from "@apollo/client";

export const GET_MASTER_OBLIGATION_DETAIL_QUERY = gql`
  query GetMasterObligation($id: ID!) {
    getMasterObligation(id: $id) {
      id
      assetType
      category
      description
      companyId
      isCustom
      evidence
      methods
      tags
      citations
      riskTier
      frequency
    }
  }
`;

export const GET_TASK_LIST_QUERY = gql`
  query GetTaskList($assetId: ID) {
    getTaskList(assetId: $assetId) {
      id
      assetId
      obligationResultId
      userId
      dueDate
      documentId
      documentType
      frequency
      description
      data
      result
      createdAt
      updatedAt
      document {
        id
        name
        mime_type
        size
        storage_path
        source
        checksum
        created_at
        updated_at
      }
      obligationResult {
        id
        assetId
        obligationId
        result
        userId
        remarks
        tags
        frequency
        riskTier
        trade
        description
        createdAt
        updatedAt
      }
      masterObligation {
        id
        assetType
        category
        obligation
        description
        evidence
        methods
        tags
        citations
        riskTier
        frequency
      }
      asset {
        id
        name
        locationIds
        locationAddress
        productId
        productTypeId
        productTypeName
        productCategoryId
        productCategoryName
      }
    }
  }
`;

export const GET_TASK_LIST_WITH_RELATIONS_QUERY = gql`
  query GetTaskListWithRelations($assetId: ID!) {
    getTaskListWithRelations(assetId: $assetId) {
      id
      assetId
      obligationResultId
      userId
      dueDate
      documentId
      documentType
      frequency
      description
      data
      result
      createdAt
      updatedAt
      document {
        id
        name
        mime_type
        size
        storage_path
        source
        checksum
        created_at
        updated_at
      }
      obligationResult {
        id
        assetId
        obligationId
        result
        remarks
        tags
        frequency
        riskTier
        trade
        description
        createdAt
        updatedAt
      }
      masterObligation {
        id
        assetType
        category
        obligation
        description
        evidence
        methods
        tags
        citations
        riskTier
        frequency
      }
    }
  }
`;

export const GET_TASK_LIST_SUMMARY_QUERY = gql`
  query GetTaskListSummary {
    getTaskListSummary {
      assetId
      assetName
      totalTasks
      completedTasks
      status
    }
  }
`;

/** Single request for company Obligations page (replaces separate obligations + tasks + obligation-results calls). */

export const GET_OBLIGATIONS_PAGE_DATA_QUERY = gql`
  query GetObligationsPageData(
    $assetIds: [ID!]
    $assetType: String
    $category: String
    $riskTier: String
    $status: String
    $obligationLimit: Int = 10
    $obligationOffset: Int = 0
    $taskLimit: Int = 200
    $taskOffset: Int = 0
  ) {
    getObligationsPageData(
      assetIds: $assetIds
      assetType: $assetType
      category: $category
      riskTier: $riskTier
      status: $status
      obligationLimit: $obligationLimit
      obligationOffset: $obligationOffset
      taskLimit: $taskLimit
      taskOffset: $taskOffset
    ) {
      totalCount
      pageSize
      offset
      hasMore
      masterObligations {
        id
        assetType
        category
        obligation
        description
        evidence
        methods
        tags
        citations
        riskTier
        frequency
      }
      tasks {
        id
        assetId
        obligationResultId
        userId
        dueDate
        documentId
        documentType
        frequency
        description
        data
        result
        createdAt
        updatedAt
        document {
          id
          name
          mime_type
          size
          storage_path
          source
          checksum
          created_at
          updated_at
        }
        obligationResult {
          id
          assetId
          obligationId
          result
          userId
          remarks
          tags
          frequency
          riskTier
          trade
          description
          createdAt
          updatedAt
        }
        masterObligation {
          id
          assetType
          category
          obligation
          description
          evidence
          methods
          tags
          citations
          riskTier
          frequency
        }
        asset {
          id
          name
          locationIds
          locationAddress
          productId
          productTypeId
          productTypeName
          productCategoryId
          productCategoryName
        }
      }
    }
  }
`;

export const GET_CATALOG_OBLIGATIONS_QUERY = gql`
  query GetCatalogObligations(
    $locationId: ID
    $productTypeId: ID
    $productId: ID
    $status: String
  ) {
    getObligations(
      locationId: $locationId
      productTypeId: $productTypeId
      productId: $productId
      status: $status
    ) {
      obligationId
      obligation
      description
      type
      evidences
      obligationClass
      fulfillmentMode
      consequenceOfBreach
      locationId
      productTypeId
      productId
      jurisdictions
      status
      createdAt
      updatedAt
    }
  }
`;

export const GET_OBLIGATION_DETAIL_QUERY = gql`
  query GetObligationDetail($obligationId: ID!) {
    getObligation(obligationId: $obligationId) {
      obligationId
      obligation
      description
      type
      evidences
      obligationClass
      fulfillmentMode
      consequenceOfBreach
      locationId
      productTypeId
      productId
      jurisdictions
      status
      source
      createdAt
      updatedAt
    }
  }
`;

// Browse/verification view (see pages/obligationsBrowser) — same shape as
// GET_CATALOG_OBLIGATIONS_QUERY plus `source`, needed to filter by which
// lens/rulebook/base-pass generated an obligation.
export const GET_OBLIGATIONS_BROWSER_QUERY = gql`
  query GetObligationsBrowser(
    $locationId: ID
    $productTypeId: ID
    $productId: ID
    $status: String
  ) {
    getObligations(
      locationId: $locationId
      productTypeId: $productTypeId
      productId: $productId
      status: $status
    ) {
      obligationId
      obligation
      source
      locationId
      locationName
      productTypeId
      productTypeName
      productId
      status
      jurisdictions
      createdAt
      updatedAt
    }
  }
`;

export const GET_ACTIVE_OBLIGATIONS_PROMPTS_QUERY = gql`
  query GetActiveObligationsPrompts {
    getPrompts(module: OBLIGATIONS) {
      id
      name
      description
      activeVersion {
        id
        isActive
      }
    }
  }
`;

export const GET_COMPLIANCE_LENSES_QUERY = gql`
  query GetComplianceLenses {
    getComplianceLenses {
      id
      key
      name
      standards
      isPrimary
      isEnabled
      isSystem
      locationStandards
    }
  }
`;

export const GET_TASK_LISTS_BY_ASSETS_QUERY = gql`
  query GetTaskListsByAssets($assetIds: [ID!], $inspectionOrderId: ID, $limit: Int = 50, $offset: Int = 0) {
    getTaskListsByAssets(assetIds: $assetIds, inspectionOrderId: $inspectionOrderId, limit: $limit, offset: $offset) {
      id
      assetId
      obligationResultId
      userId
      dueDate
      documentId
      documentType
      frequency
      description
      data
      taskData
      result
      createdAt
      updatedAt
      document {
        id
        name
        mime_type
        size
        storage_path
        source
        checksum
        created_at
        updated_at
      }
      obligationResult {
        id
        assetId
        obligationId
        result
        userId
        remarks
        tags
        frequency
        riskTier
        trade
        description
        createdAt
        updatedAt
      }
      masterObligation {
        id
        assetType
        category
        obligation
        description
        evidence
        methods
        tags
        citations
        riskTier
        frequency
      }
      asset {
        id
        name
        locationIds
        locationAddress
        productId
        productTypeId
        productTypeName
        productCategoryId
        productCategoryName
      }
    }
  }
`;

/** Same as getObligationResults with masterObligation — used when getTaskListWithRelations is unavailable. */

export const GET_OBLIGATION_RESULTS_WITH_MASTER_QUERY = gql`
  query GetObligationResultsWithMaster($assetId: ID!) {
    getObligationResults(assetId: $assetId) {
      id
      assetId
      obligationId
      result
      remarks
      documentId
      tags
      frequency
      riskTier
      trade
      description
      createdAt
      updatedAt
      masterObligation {
        id
        assetType
        category
        obligation
        description
        evidence
        methods
        tags
        citations
        riskTier
        frequency
      }
      obligation {
        obligationId
        obligation
        description
        type
        evidences
        obligationClass
        fulfillmentMode
        consequenceOfBreach
        jurisdictions
        status
        source
      }
    }
  }
`;

/** Obligation results linked to a specific evidence document (document_id), for the document detail Obligations tab. */
export const GET_OBLIGATION_RESULTS_BY_DOCUMENT_QUERY = gql`
  query GetObligationResultsByDocument(
    $documentId: ID!
    $inspectionOrderId: ID
    $periodStart: String
  ) {
    getObligationResultsByDocument(
      documentId: $documentId
      inspectionOrderId: $inspectionOrderId
      periodStart: $periodStart
    ) {
      id
      obligationId
      result
      documentId
      tags
      remarks
      frequency
      riskTier
      description
      createdAt
      obligation {
        obligationId
        obligation
        description
        obligationClass
        fulfillmentMode
        status
      }
    }
  }
`;

export const GET_OBLIGATIONS_BY_LOCATION_AND_STANDARD = gql`
  query GetObligationsByLocationAndStandard($locationId: ID!, $standardKey: String!) {
    getObligationsByLocationAndStandard(locationId: $locationId, standardKey: $standardKey) {
      obligationId
      obligation
      description
      origins
      type
      standard
      reference
      evidences
      obligationClass
      fulfillmentMode
      consequenceOfBreach
      flags
      locationId
      productTypeId
      productId
      triggerType
      cadenceInterval
      cadenceLabel
      anchorDate
      standardKey
      status
      decidedBy
      decidedAt
      createdAt
      updatedAt
    }
  }
`;
